import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Building2Icon, CalendarClockIcon, CalendarDaysIcon, ClipboardCheckIcon, DollarSignIcon, LandmarkIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { StateComplianceData } from './types';
interface CompanyStateComplianceModalProps {
  stateName: string;
  isOpen: boolean;
  onClose: () => void;
  compliance: StateComplianceData;
  onUpdateCompliance: (compliance: StateComplianceData) => void;
}
interface DateFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  icon: typeof CalendarDaysIcon;
  accentClass: string;
}
export function CompanyStateComplianceModal({
  stateName,
  isOpen,
  onClose,
  compliance,
  onUpdateCompliance
}: CompanyStateComplianceModalProps) {
  const updateField = <K extends keyof StateComplianceData,>(field: K, value: StateComplianceData[K]) => {
    onUpdateCompliance({
      ...compliance,
      [field]: value
    });
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-24" data-mp="2hrrhdv6ulif2">
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6" data-id="element-25" data-mp="2hr9am4mk7lsw">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/65 backdrop-blur-sm" data-id="element-26" data-mp="2mlvqol3l62jb" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 320
      }} role="dialog" aria-modal="true" aria-labelledby="company-compliance-title" className="relative flex w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-emerald-500/25 bg-slate-900 shadow-2xl shadow-black/40 max-h-[90vh]" data-id="element-27" data-mp="30jtrenozi0gc">
          <div className="flex items-start justify-between gap-4 border-b border-slate-800 bg-slate-900/80 px-5 py-4 sm:px-6" data-id="element-28" data-mp="1l3l7asr5pxte">
            <div className="flex min-w-0 items-center gap-3" data-id="element-29" data-mp="3mu3izdo1h7eq">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-emerald-500/25 bg-emerald-500/10" data-id="element-30" data-mp="3htxozquar8th">
                <Building2Icon className="h-5 w-5 text-emerald-400" data-id="element-31" data-mp="3cztgh35dusqa" />
              </div>
              <div className="min-w-0" data-id="element-32" data-mp="38yhwpe96x1q0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-400" data-id="element-33" data-mp="34210rxvr1j2d">
                  Olympian Mortgage · Company compliance
                </p>
                <h2 id="company-compliance-title" className="mt-0.5 text-xl font-bold text-white" data-id="element-34" data-mp="2u3bt0xxkhezu">
                  {stateName}
                </h2>
              </div>
            </div>
            <button onClick={onClose} className="-mr-2 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400" aria-label="Close company compliance details" data-id="element-35" data-mp="2q42znu6fvj4t">
              <XIcon className="h-5 w-5" data-id="element-36" data-mp="2b6tf2cxhqtox" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 sm:p-6 custom-scrollbar" data-id="element-37" data-mp="2b4qex02mjk6h">
            <div className="mb-6 rounded-xl border border-emerald-500/15 bg-emerald-500/[0.05] px-4 py-3" data-id="element-38" data-mp="2fyl6sqsdv5gn">
              <div className="flex gap-2.5" data-id="element-39" data-mp="2k1hirynf43bm">
                <ShieldCheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" data-id="element-40" data-mp="1n9nseyralst6" />
                <p className="text-sm leading-5 text-slate-300" data-id="element-41" data-mp="1s7p6t6drx3yb">
                  Track the organization-level requirements for {stateName}. These details apply to Olympian Mortgage, not to an individual loan officer.
                </p>
              </div>
            </div>

            <div className="space-y-6" data-id="element-42" data-mp="216j3ewr3tubv">
              <section aria-labelledby="renewal-dates-heading" data-id="element-43" data-mp="37nxrp3jfdamb">
                <h3 id="renewal-dates-heading" className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400" data-id="element-44" data-mp="3co28kuo8w7d2">
                  Renewal & audit dates
                </h3>
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-45" data-mp="3b8q2j1mkt75k">
                  <DateField label="License renewal date" value={compliance.renewalDate} onChange={value => updateField('renewalDate', value)} icon={CalendarClockIcon} accentClass="text-emerald-400" data-id="element-46" data-mp="3693oipyxbmvp" />
                  <DateField label="Secretary of State renewal" value={compliance.secretaryOfStateRenewalDate} onChange={value => updateField('secretaryOfStateRenewalDate', value)} icon={LandmarkIcon} accentClass="text-sky-400" data-id="element-47" data-mp="3u3obnlsaqjwv" />
                  <DateField label="Audit date" value={compliance.auditDate} onChange={value => updateField('auditDate', value)} icon={ClipboardCheckIcon} accentClass="text-amber-400" data-id="element-48" data-mp="gq34eg923sd7" />
                </div>
              </section>

              <section className="border-t border-slate-800 pt-6" aria-labelledby="bond-heading" data-id="element-49" data-mp="10ak3lya72vyc">
                <h3 id="bond-heading" className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400" data-id="element-50" data-mp="mey8d9ddbfxh">
                  Surety bond
                </h3>
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-51" data-mp="cmabyfqavdza">
                  <div data-id="element-52" data-mp="hdinbhb6x9f3">
                    <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-53" data-mp="heu0u8uftvje">
                      Bond amount
                    </label>
                    <div className="flex items-center rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2.5 transition-colors focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30" data-id="element-54" data-mp="2n1l3vqqefh5">
                      <DollarSignIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-55" data-mp="3usf8hfhlap3l" />
                      <input type="number" min="0" value={compliance.suretyBondAmount ?? ''} onChange={event => {
                      const rawValue = event.target.value;
                      updateField('suretyBondAmount', rawValue === '' ? null : Number(rawValue));
                    }} placeholder="0" className="w-full bg-transparent text-sm text-slate-100 tabular-nums outline-none placeholder:text-slate-600" aria-label="Surety bond amount" data-id="element-56" data-mp="20c6rq8schpwm" />
                    </div>
                  </div>
                  <div data-id="element-57" data-mp="yvhe727okqjv">
                    <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-58" data-mp="yw0opxv04cnv">
                      Held by
                    </label>
                    <div className="flex items-center rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2.5 transition-colors focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30" data-id="element-59" data-mp="tv33tdilx4io">
                      <ShieldCheckIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-60" data-mp="3ekfxdga91uoe" />
                      <input type="text" value={compliance.suretyBondHeldBy ?? ''} onChange={event => updateField('suretyBondHeldBy', event.target.value || null)} placeholder="Surety provider or holder" className="w-full bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-600" data-id="element-61" data-mp="3eila3vbi9qh5" />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className="flex justify-end border-t border-slate-800 bg-slate-900/80 px-5 py-4 sm:px-6" data-id="element-62" data-mp="23vyx7d12ozim">
            <button onClick={onClose} className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-950/40 transition-colors hover:bg-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300" data-id="element-63" data-mp="27w9a79na8q6z">
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}
function DateField({
  label,
  value,
  onChange,
  icon: Icon,
  accentClass
}: DateFieldProps) {
  return <div data-id="element-64" data-mp="162ppoze07wq0">
      <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-65" data-mp="111s227a86ah4">{label}</label>
      <div className="flex items-center rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2.5 transition-colors focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30" data-id="element-66" data-mp="1144rt6m85p4q">
        <Icon className={`mr-2 h-4 w-4 flex-shrink-0 ${accentClass}`} data-id="element-67" data-mp="2qgq5lzflk5mf" />
        <input type="date" value={value ?? ''} onChange={event => onChange(event.target.value || null)} className="w-full bg-transparent text-sm text-slate-100 outline-none" style={{
        colorScheme: 'dark'
      }} data-id="element-68" data-mp="2qit292b98x04" />
      </div>
    </div>;
}